import { useEffect, useState } from "react";
import type { InventoryRow } from "@school-sales-ai/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAdjustStock, useInventoryMovements } from "@/hooks/useInventory";

const KIND_LABEL: Record<string, string> = {
  initial: "Conteo inicial",
  adjustment: "Ajuste",
  reserve: "Apartado por pedido",
  release: "Liberado",
  fulfill: "Entregado",
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Sets an absolute count — what someone just counted on the shelf — and shows
 * the ledger for that product.
 *
 * A reason is asked for and not required: an inventory history whose rows say
 * nothing is only marginally better than no history, but refusing a correction
 * because the box is empty means the number stays wrong.
 */
export function AdjustStockDialog({
  row,
  open,
  onOpenChange,
}: {
  row: InventoryRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const [onHand, setOnHand] = useState("");
  const [reason, setReason] = useState("");
  const adjust = useAdjustStock();
  const { data: movements } = useInventoryMovements(open ? (row?.productId ?? null) : null, 1);

  useEffect(() => {
    if (open) {
      setOnHand(row?.onHand === null || row?.onHand === undefined ? "" : String(row.onHand));
      setReason("");
    }
  }, [open, row]);

  const parsed = Number(onHand);
  const valid = onHand.trim() !== "" && Number.isInteger(parsed) && parsed >= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Existencias — {row?.productName}</DialogTitle>
          <DialogDescription>
            Captura cuántas unidades hay físicamente. Las unidades apartadas por pedidos
            confirmados no se tocan.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <label className="text-[13px] font-medium text-ink-800" htmlFor="stock-on-hand">
            Unidades en existencia
          </label>
          <input
            id="stock-on-hand"
            className="w-32 rounded-lg border border-ink-200 px-3 py-2 text-sm"
            inputMode="numeric"
            value={onHand}
            aria-label="Unidades en existencia"
            onChange={(e) => setOnHand(e.target.value.replace(/[^\d]/g, ""))}
          />
          <input
            className="rounded-lg border border-ink-200 px-3 py-2 text-sm"
            placeholder="Motivo (opcional): recuento, merma, entrada de proveedor…"
            value={reason}
            aria-label="Motivo del ajuste"
            onChange={(e) => setReason(e.target.value)}
          />

          {movements && movements.items.length > 0 && (
            <div className="mt-2">
              <p className="mb-1 text-[12.5px] font-medium text-ink-700">Movimientos</p>
              <ul className="max-h-48 overflow-y-auto text-[12.5px] text-ink-600">
                {movements.items.map((m) => (
                  <li key={m.id} className="border-b border-ink-050 py-1.5">
                    <span className="text-ink-900">{KIND_LABEL[m.kind] ?? m.kind}</span>{" "}
                    {m.quantity > 0 ? `+${m.quantity}` : m.quantity} → {m.onHandAfter} en
                    existencia
                    {m.reason ? ` · ${m.reason}` : ""}
                    <span className="ml-2 text-ink-500">{formatWhen(m.createdAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-lg border border-ink-200 px-4 py-2 text-sm font-medium text-ink-700"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!valid || adjust.isPending || !row}
            onClick={() => {
              if (!row) return;
              adjust.mutate(
                { productId: row.productId, onHand: parsed, reason: reason || undefined },
                { onSuccess: () => onOpenChange(false) },
              );
            }}
            className="rounded-lg bg-lime px-4 py-2 text-sm font-semibold text-moss disabled:opacity-40"
          >
            Guardar
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
